import { queryOptions } from '@tanstack/react-query';

/** A security contact that passed validation, ready to render as a link. */
export interface SecurityContact {
  kind: 'email' | 'web';
  /** The validated URI, safe for an href. */
  href: string;
  /** What the link shows: the address, or the host of the web page. */
  label: string;
}

/**
 * parseSecurityContact returns the first Contact: value of a security.txt
 * (RFC 9116) that is a mailto: address or an https: URL, and nothing else.
 * Any other text in the file, and any other scheme (javascript:, data:, http:),
 * is ignored: the page renders only the validated URI.
 */
export function parseSecurityContact(text: string): SecurityContact | null {
  for (const line of text.split(/\r?\n/)) {
    const match = /^contact:[ \t]*(.+)$/i.exec(line);
    const value = match?.[1]?.trim();
    if (!value) continue;
    const contact = validateContact(value);
    if (contact) return contact;
  }
  return null;
}

function validateContact(value: string): SecurityContact | null {
  if (/\s/.test(value)) return null;
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return null;
  }
  if (url.protocol === 'https:' && url.hostname !== '') {
    return { kind: 'web', href: url.href, label: url.host };
  }
  if (url.protocol === 'mailto:') {
    const address = decodeURIComponent(url.pathname);
    if (!/^[^@\s,<>]+@[^@\s,<>]+$/.test(address)) return null;
    return {
      kind: 'email',
      href: `mailto:${encodeURIComponent(address).replace('%40', '@')}`,
      label: address,
    };
  }
  return null;
}

/**
 * The deployment's own security.txt. A 404 means the operator has not set a
 * contact, which is a normal answer and resolves to null.
 */
export const securityContactQuery = queryOptions({
  queryKey: ['security-contact'],
  // A plain fetch rather than ApiClient: this is a text file outside the API.
  queryFn: async (): Promise<SecurityContact | null> => {
    const response = await fetch('/.well-known/security.txt', { credentials: 'omit' });
    if (response.status === 404) return null;
    if (!response.ok) throw new Error(`security.txt: HTTP ${String(response.status)}`);
    return parseSecurityContact(await response.text());
  },
  staleTime: 5 * 60_000,
});
